import React, { useEffect, useReducer, useState, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import { LuPencil, LuTrash2 } from "react-icons/lu";

import MainContainer from "../../components/MainContainer";
import {
  CrudListPage,
  CrudActionButton,
  useCrudListStyles,
} from "../../components/CrudListPage";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import QueueModal from "../../components/QueueModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    }
    return [queue, ...state];
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useLocalStyles = makeStyles(() => ({
  colorSwatch: {
    width: 60,
    height: 20,
    borderRadius: 4,
    display: "inline-block",
  },
  colorCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  truncateText: {
    maxWidth: 280,
    display: "block",
  },
}));

const Queues = () => {
  const classes = useCrudListStyles({ tableMinWidth: 800 });
  const localClasses = useLocalStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    };
    socket.on(`company-${companyId}-queue`, onQueueEvent);

    return () => {
      socket.off(`company-${companyId}-queue`, onQueueEvent);
    };
  }, [socket, companyId]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("Queue deleted successfully!"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <MainContainer>
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <CrudListPage
          title={`${i18n.t("queues.title")} (${queues.length})`}
          tableMinWidth={800}
          beforeContent={
            <>
              <ConfirmationModal
                title={
                  selectedQueue &&
                  `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
                }
                open={confirmModalOpen}
                onClose={handleCloseConfirmationModal}
                onConfirm={() => handleDeleteQueue(selectedQueue.id)}
              >
                {i18n.t("queues.confirmationModal.deleteMessage")}
              </ConfirmationModal>
              <QueueModal
                open={queueModalOpen}
                onClose={handleCloseQueueModal}
                queueId={selectedQueue?.id}
                onEdit={(res) => {
                  if (res) {
                    setTimeout(() => {
                      handleEditQueue(res);
                    }, 500);
                  }
                }}
              />
            </>
          }
          toolbar={
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryButton}
              onClick={handleOpenQueueModal}
            >
              {i18n.t("queues.buttons.add")}
            </Button>
          }
        >
          <Table size="small" stickyHeader className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell align="center">
                  {i18n.t("queues.table.ID")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("queues.table.name")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("queues.table.color")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("queues.table.orderQueue")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("queues.table.greeting")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("queues.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queues.map((queue) => (
                <TableRow key={queue.id}>
                  <TableCell align="center">{queue.id}</TableCell>
                  <TableCell align="center">{queue.name}</TableCell>
                  <TableCell align="center">
                    <div className={localClasses.colorCell}>
                      <span
                        className={localClasses.colorSwatch}
                        style={{ backgroundColor: queue.color }}
                      />
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      className={localClasses.truncateText}
                      noWrap
                      variant="body2"
                    >
                      {queue.orderQueue}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      className={localClasses.truncateText}
                      noWrap
                      variant="body2"
                    >
                      {queue.greetingMessage}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" className={classes.actionsCell}>
                    <CrudActionButton
                      classes={classes}
                      icon={LuPencil}
                      onClick={() => handleEditQueue(queue)}
                      ariaLabel="Editar fila"
                    />
                    <CrudActionButton
                      classes={classes}
                      icon={LuTrash2}
                      onClick={() => {
                        setSelectedQueue(queue);
                        setConfirmModalOpen(true);
                      }}
                      ariaLabel="Excluir fila"
                      danger
                    />
                  </TableCell>
                </TableRow>
              ))}
              {loading &&
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRowSkeleton key={`skeleton-${idx}`} columns={6} />
                ))}
            </TableBody>
          </Table>
        </CrudListPage>
      )}
    </MainContainer>
  );
};

export default Queues;
